import React, { useState } from 'react';
import { ExternalReview } from '../../../types';

interface ProjectExternalReviewsProps {
  externalReviews: ExternalReview[];
  canEdit: boolean;
  onRequestReview: (data: { reviewerEmail: string; reviewerName: string; expirationDays: number }) => Promise<void>;
  onViewDetails: (review: ExternalReview) => void;
  getReviewStatusColor: (status: string) => string;
}

const ProjectExternalReviews: React.FC<ProjectExternalReviewsProps> = ({
  externalReviews,
  canEdit,
  onRequestReview,
  onViewDetails,
  getReviewStatusColor,
}) => {
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewFormData, setReviewFormData] = useState({
    reviewerEmail: '',
    reviewerName: '',
    expirationDays: 30,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reviewFormData.reviewerEmail.trim()) {
      setError('Email is required');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      await onRequestReview(reviewFormData);
      setReviewFormData({ reviewerEmail: '', reviewerName: '', expirationDays: 30 });
      setShowReviewForm(false);
    } catch (err: any) {
      console.error('Error requesting review:', err);
      setError(err.response?.data?.message || 'Failed to request review');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700">
          External Reviews {externalReviews.length > 0 && (
            <span className="text-gray-500 font-normal">({externalReviews.length})</span>
          )}
        </h3>
        {!showReviewForm && canEdit && (
          <button
            onClick={() => setShowReviewForm(true)}
            className="text-sm text-blue-600 hover:text-blue-700"
          >
            + Request review
          </button>
        )}
      </div>

      {showReviewForm && (
        <form onSubmit={handleSubmit} className="mb-3 bg-gray-50 rounded-lg p-4">
          <div className="space-y-3">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                {error}
              </div>
            )}
            <input
              type="email"
              placeholder="Reviewer email..."
              value={reviewFormData.reviewerEmail}
              onChange={(e) => setReviewFormData({ ...reviewFormData, reviewerEmail: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
            <input
              type="text"
              placeholder="Reviewer name (optional)..."
              value={reviewFormData.reviewerName}
              onChange={(e) => setReviewFormData({ ...reviewFormData, reviewerName: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:bg-gray-400"
              >
                {submitting ? 'Sending...' : 'Send Request'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowReviewForm(false);
                  setReviewFormData({ reviewerEmail: '', reviewerName: '', expirationDays: 30 });
                  setError('');
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      )}

      {externalReviews.length > 0 && (
        <div className="space-y-2">
          {externalReviews.map((review) => (
            <div key={review._id} className="p-3 bg-gray-50 rounded hover:bg-gray-100">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {review.reviewerName || review.reviewerEmail}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">{review.reviewerEmail}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`px-2 py-0.5 text-xs rounded ${getReviewStatusColor(review.status)}`}>
                      {review.status.toUpperCase()}
                    </span>
                    {review.rating && (
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-600">{review.rating}/5</span>
                        <svg className="w-3 h-3 text-yellow-400 fill-current" viewBox="0 0 24 24">
                          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
                {canEdit && review.status === 'completed' && (
                  <button
                    onClick={() => onViewDetails(review)}
                    className="text-xs text-blue-600 hover:text-blue-700"
                  >
                    View Details
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {externalReviews.length === 0 && !showReviewForm && (
        <p className="text-sm text-gray-500">No external reviews yet</p>
      )}
    </div>
  );
};

export default ProjectExternalReviews;
